import { useEffect, useMemo, useState } from 'react';
import { orderBy } from 'firebase/firestore';
import Lozenge from '@atlaskit/lozenge';
import Textfield from '@atlaskit/textfield';
import Toggle from '@atlaskit/toggle';
import { useFlags } from '@atlaskit/flag';
import PageHeader from 'shared/components/page-header/page-header';
import SharedButton from 'shared/components/buttons/button';
import { JotformSubmissionsApiService, WebhookSettingsApiService } from 'core/api';
import DevToolsApiService from 'core/api/dev-tools/dev-tools-api.service';
import { IJotformForwardResult, IJotformSettingsDao, IJotformSubmissionDao } from 'core/api/types/jotform.interface';
import { useCollection } from 'core/hooks/useCollection';
import { useDocument } from 'core/hooks/useDocument';
import { showErrorFlag, showSuccessFlag } from 'core/utilities/flags-helper';
import DevToolsNav from './dev-tools-nav';

/**
 * The Jotform webhook, from the dev side.
 *
 * The customer is given the URL at the top once, and it never changes: the
 * webhook stores every submission, then either processes it in the cloud or —
 * with forwarding on — relays it through a tunnel to the functions emulator on
 * a laptop. Anything stored can be replayed to local code, which is how the
 * parser gets written against real submissions rather than guessed ones.
 *
 * Settings and submissions are read straight from Firestore; every change goes
 * through `devJotformRelay`.
 */

const EMULATOR_PATH = '/pride-pharmacy-delivery-dev/europe-west2/jotformWebhook';

const STATUS_APPEARANCE: Record<IJotformSubmissionDao['status'], 'success' | 'inprogress' | 'removed'> = {
  stored: 'inprogress',
  forwarded: 'success',
  forwardFailed: 'removed',
};

const STATUS_LABEL: Record<IJotformSubmissionDao['status'], string> = {
  stored: 'Stored',
  forwarded: 'Forwarded',
  forwardFailed: 'Forward failed',
};

const when = (result?: { at?: { toDate: () => Date } }) => result?.at?.toDate().toLocaleString('en-GB') ?? '';

const describeForward = (result: IJotformForwardResult) =>
  result.error ? `failed: ${result.error}` : `HTTP ${result.status} — ${result.response ?? ''}`;

/** `rawRequest` is JSON inside a form field; shown indented when it parses. */
const pretty = (text?: string) => {
  if (!text) return '';
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
};

const JotformRelay = () => {
  const flags = useFlags();
  const { data: settings } = useDocument<IJotformSettingsDao>({
    apiService: WebhookSettingsApiService,
    uid: 'jotform',
  });

  const constraints = useMemo(() => [orderBy('receivedAt', 'desc')], []);
  const { data: submissions } = useCollection<IJotformSubmissionDao>({
    apiService: JotformSubmissionsApiService,
    constraints,
    initialLimit: 25,
  });

  const [forwardEnabled, setForwardEnabled] = useState(false);
  const [forwardTo, setForwardTo] = useState('');
  const [busy, setBusy] = useState<string>();
  const [open, setOpen] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setForwardEnabled(settings?.forwardEnabled === true);
    setForwardTo(settings?.forwardTo ?? '');
  }, [settings]);

  const url = settings?.key ? `${window.location.origin}/webhooks/jotform?key=${settings.key}` : undefined;

  const run = async (label: string, action: () => Promise<unknown>, success?: string) => {
    setBusy(label);
    try {
      await action();
      if (success) showSuccessFlag(success, '', flags);
    } catch (error) {
      showErrorFlag('That did not work', (error as Error).message, flags);
    } finally {
      setBusy(undefined);
    }
  };

  const copy = () =>
    url &&
    navigator.clipboard.writeText(url).then(
      () => showSuccessFlag('Copied', 'The webhook URL is on your clipboard.', flags),
      () => showErrorFlag('Could not copy', 'Select the URL and copy it by hand.', flags),
    );

  // With forwarding on, a replay goes to your laptop; with it off, it imports
  // again in the cloud. Either way an already-imported submission is found
  // rather than booked twice.
  const forwarding = settings?.forwardEnabled === true && Boolean(settings?.forwardTo);
  const replay = (uid: string) =>
    run(
      uid,
      async () => {
        const result = await DevToolsApiService.jotformRelay({ action: 'replay', uid });
        if (!result.ok) throw new Error(result.error ?? `The local function answered HTTP ${result.status}.`);
      },
      forwarding ? 'Replayed to local' : 'Imported',
    );

  return (
    <>
      <PageHeader
        title='Jotform relay'
        subtitle='One webhook URL for the customer, relayed to your laptop when you want.'
      />
      <DevToolsNav />

      <div className='mt-6 overflow-y-auto pb-10'>
        <div className='rounded border p-5' data-testid='jotform-url'>
          <p className='body-02 font-semibold'>Webhook URL</p>
          <p className='body-01 mt-1 text-gray-600'>
            Give this to the customer for the dev form's webhook. The key in it is the only authentication — Jotform
            cannot sign its webhooks — so regenerating it breaks the URL they have.
          </p>

          {url ? (
            <div className='mt-3 flex items-center gap-3'>
              <code className='body-01 flex-1 break-all rounded bg-gray-50 p-2'>{url}</code>
              <SharedButton type='button' appearance='primary' label='Copy' onClick={copy} />
            </div>
          ) : (
            <p className='body-01 mt-3 text-amber-800'>No key yet, so the webhook refuses everything.</p>
          )}

          <div className='mt-3'>
            <SharedButton
              type='button'
              appearance={url ? 'subtle' : 'primary'}
              label={url ? 'Regenerate key (breaks the current URL)' : 'Generate key'}
              isLoading={busy === 'key'}
              onClick={() =>
                run('key', () => DevToolsApiService.jotformRelay({ action: 'generateKey' }), 'New key saved')
              }
            />
          </div>
        </div>

        <div className='mt-6 rounded border p-5' data-testid='jotform-forwarding'>
          <p className='body-02 font-semibold'>Forward to local</p>
          <p className='body-01 mt-1 text-gray-600'>
            Each submission is stored in dev first, then sent on to your emulator. If your laptop is off the forward
            fails, the submission is still kept, and you can replay it later.
          </p>
          <pre className='body-01 mt-3 overflow-auto rounded bg-gray-50 p-3 text-xs'>
            {`cd functions && npm run serve        # the emulator, on :5001\nngrok http --url=<your-domain> 5001  # your free static ngrok domain`}
          </pre>

          <label className='body-01 mb-1 mt-4 block font-semibold text-gray-700'>Tunnel address</label>
          <Textfield
            value={forwardTo}
            onChange={(event) => setForwardTo((event.target as HTMLInputElement).value)}
            placeholder={`https://<your-domain>.ngrok-free.app${EMULATOR_PATH}`}
            testId='jotform-forward-to'
          />

          <div className='mt-3 flex items-center gap-2'>
            <Toggle
              isChecked={forwardEnabled}
              onChange={() => setForwardEnabled((current) => !current)}
              testId='jotform-forward-enabled'
            />
            <span className='body-01'>Forward submissions to this address</span>
          </div>

          <div className='mt-4'>
            <SharedButton
              type='button'
              appearance='primary'
              label='Save'
              isLoading={busy === 'forwarding'}
              onClick={() =>
                run(
                  'forwarding',
                  () => DevToolsApiService.jotformRelay({ action: 'saveForwarding', forwardEnabled, forwardTo }),
                  forwardEnabled ? 'Forwarding on' : 'Forwarding off',
                )
              }
            />
          </div>
        </div>

        <div className='mt-6' data-testid='jotform-submissions'>
          <p className='body-02 mb-3 font-semibold'>Latest submissions</p>
          {!submissions?.length && <p className='body-01 text-gray-600'>Nothing has arrived yet.</p>}

          {submissions?.map((submission) => (
            <div key={submission.uid} className='mb-3 rounded border p-4'>
              <div className='flex items-start justify-between gap-3'>
                <div>
                  <p className='body-02 font-semibold'>{submission.formTitle ?? submission.formID ?? 'Unknown form'}</p>
                  <p className='body-01 text-gray-600'>
                    {when({ at: submission.receivedAt })} · submission {submission.submissionID ?? '—'}
                  </p>
                  {submission.forward && (
                    <p className='body-01 mt-1 text-gray-600'>Forward: {describeForward(submission.forward)}</p>
                  )}
                  {submission.imported && (
                    <p className='body-01 mt-1 text-gray-600' data-testid='jotform-imported'>
                      {submission.imported.parseError
                        ? `Could not be read (${submission.imported.by}): ${submission.imported.parseError}`
                        : `${submission.imported.duplicate ? 'Already imported as' : 'Imported as'} request ${submission.imported.requestUid.slice(0, 8)} — ${submission.imported.status} (${submission.imported.by}, ${when(submission.imported)})`}
                    </p>
                  )}
                  {submission.importError && (
                    <p className='body-01 mt-1 text-red-700'>Import failed: {submission.importError}</p>
                  )}
                  {submission.lastReplay && (
                    <p className='body-01 mt-1 text-gray-600'>
                      Last replay, {when(submission.lastReplay)}: {describeForward(submission.lastReplay)}
                    </p>
                  )}
                </div>
                <div className='flex shrink-0 items-center gap-2'>
                  <Lozenge appearance={STATUS_APPEARANCE[submission.status]}>{STATUS_LABEL[submission.status]}</Lozenge>
                  <SharedButton
                    type='button'
                    appearance='default'
                    label={forwarding ? 'Replay to local' : 'Import again'}
                    isLoading={busy === submission.uid}
                    disabled={forwarding && !submission.rawBodyBase64}
                    onClick={() => replay(submission.uid)}
                  />
                </div>
              </div>

              <button
                type='button'
                className='body-01 mt-2 text-blue-700 underline'
                onClick={() => setOpen((current) => ({ ...current, [submission.uid]: !current[submission.uid] }))}
              >
                {open[submission.uid] ? 'Hide what was sent' : 'Show what was sent'}
              </button>

              {open[submission.uid] && (
                <div className='mt-2'>
                  {submission.fields.pretty && (
                    <pre className='mb-2 whitespace-pre-wrap rounded bg-gray-50 p-3 text-xs'>
                      {submission.fields.pretty}
                    </pre>
                  )}
                  <pre className='max-h-96 overflow-auto rounded bg-gray-50 p-3 text-xs'>
                    {pretty(submission.fields.rawRequest) || JSON.stringify(submission.fields, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default JotformRelay;
